/**
 * chordAlignment.js
 *
 * Primary export: alignChordsToLyrics
 *   - Given madmom chords (with second timestamps) and user lyrics text,
 *     optionally with Whisper word timestamps, align each chord to the
 *     correct word in the user's lyrics using DP sequence alignment.
 *
 * Fallback export: evenDistribute
 *   - Simple proportional spread used when there is no timing data (MIDI).
 */
import { isWordTimingRepairEnabled } from '../config/featureFlags';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseLyricsWords(lyrics) {
  const lines = lyrics.split('\n');
  const words = [];
  lines.forEach((line, li) => {
    const trimmed = line.trim();
    if (!trimmed || /^\[.+\]/.test(trimmed)) return;
    trimmed.split(/\s+/).filter(Boolean).forEach((w, wi) => {
      words.push({ word: w, lineIndex: li, wordIndex: wi });
    });
  });
  return words;
}

/** Strip punctuation, lowercase for fuzzy comparison */
function normalize(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function median(values) {
  const nums = values
    .map(Number)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function isInstrumentalHeader(line) {
  return /^\[(instrumental|solo|guitar\s+solo)\b/i.test(String(line || '').trim());
}

function hasInstrumentalHeaderSincePreviousLyric(lyricLines, lineIndex) {
  for (let li = lineIndex - 1; li >= 0; li--) {
    const trimmed = String(lyricLines[li] || '').trim();
    if (!trimmed) continue;
    if (/^\[.+\]/.test(trimmed)) {
      if (isInstrumentalHeader(trimmed)) return true;
      continue;
    }
    return false;
  }
  return false;
}

function cloneTimestamps(timestamps) {
  return timestamps.map((ts) => (ts ? { ...ts } : ts));
}

/**
 * Repair a narrow forced-alignment failure: a first lyric word after an
 * instrumental/solo section starts in the instrumental because of vocal bleed,
 * then the rest of the phrase lands normally. This runs before chord placement
 * so the canonical timing model remains the source of truth.
 */
export function repairPostInstrumentalWordTimings(userWords, timestamps, userLyrics, chords = [], opts = {}) {
  if (!isWordTimingRepairEnabled(opts) || !Array.isArray(userWords) || !Array.isArray(timestamps)) {
    return { timestamps, repairs: [] };
  }

  const lyricLines = userLyrics ? userLyrics.split('\n') : [];
  const nextTimestamps = cloneTimestamps(timestamps);
  const repairs = [];
  const lineToWordIndices = new Map();
  userWords.forEach((word, idx) => {
    if (!lineToWordIndices.has(word.lineIndex)) lineToWordIndices.set(word.lineIndex, []);
    lineToWordIndices.get(word.lineIndex).push(idx);
  });

  for (let idx = 0; idx < userWords.length; idx++) {
    const word = userWords[idx];
    const ts = nextTimestamps[idx];
    if (!word || word.wordIndex !== 0 || !ts) continue;
    const start = Number(ts.start);
    const end = Number(ts.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    if (!hasInstrumentalHeaderSincePreviousLyric(lyricLines, word.lineIndex)) continue;

    const lineIndices = (lineToWordIndices.get(word.lineIndex) || [])
      .filter((wordIdx) => userWords[wordIdx]?.wordIndex > 0)
      .sort((a, b) => userWords[a].wordIndex - userWords[b].wordIndex);
    const following = lineIndices
      .slice(0, 4)
      .map((wordIdx) => ({ wordIdx, ts: nextTimestamps[wordIdx] }))
      .filter(({ ts: fts }) => fts && Number.isFinite(Number(fts.start)) && Number.isFinite(Number(fts.end)));
    if (following.length < 3) continue;

    const firstFollowingStart = Number(following[0].ts.start);
    const phraseEnd = Number(following[following.length - 1].ts.end);
    if (!Number.isFinite(firstFollowingStart) || !Number.isFinite(phraseEnd)) continue;

    const followingDurations = following
      .map(({ ts: fts }) => Math.max(0, Number(fts.end) - Number(fts.start)))
      .filter((d) => d > 0.02);
    const phraseMedianDur = median(followingDurations);
    if (!phraseMedianDur) continue;

    const candidateDuration = end - start;
    const earlySpan = firstFollowingStart - start;
    const clusterSpan = phraseEnd - firstFollowingStart;
    const outlierRatio = candidateDuration / Math.max(phraseMedianDur, 0.05);

    // Absolute floors only prevent tiny timing noise from being "repaired";
    // the actual decision is phrase-relative and section-boundary constrained.
    if (earlySpan < 2.0 || outlierRatio < 8 || clusterSpan > Math.max(4.0, phraseMedianDur * 12)) {
      continue;
    }

    const earlyChords = (chords || [])
      .filter((c) => c && c.chord && Number.isFinite(Number(c.time)))
      .filter((c) => Number(c.time) >= start && Number(c.time) < firstFollowingStart)
      .sort((a, b) => Number(a.time) - Number(b.time));
    let earlyChordChangeCount = 0;
    let lastChord = null;
    for (const c of earlyChords) {
      if (c.chord !== lastChord) {
        earlyChordChangeCount++;
        lastChord = c.chord;
      }
    }
    if (earlyChordChangeCount < 3) continue;

    const expectedDuration = Math.min(
      Math.max(phraseMedianDur, 0.18),
      Math.max(0.18, firstFollowingStart - end),
    );
    const maxRepairedStart = end - 0.02;
    if (maxRepairedStart <= start) continue;
    const repairedStart = Math.max(start, Math.min(maxRepairedStart, firstFollowingStart - expectedDuration));
    if (!Number.isFinite(repairedStart) || repairedStart <= start || repairedStart >= end) continue;

    nextTimestamps[idx] = {
      ...ts,
      start: Math.round(repairedStart * 1000) / 1000,
    };
    repairs.push({
      lineIndex: word.lineIndex,
      wordIndex: word.wordIndex,
      word: word.word,
      oldStart: start,
      newStart: nextTimestamps[idx].start,
      end,
      reason: 'post-instrumental-first-word-duration-outlier',
      outlierRatio: Math.round(outlierRatio * 100) / 100,
      earlyChordChangeCount,
    });
  }

  return { timestamps: nextTimestamps, repairs };
}

// ---------------------------------------------------------------------------
// DP sequence alignment (Needleman-Wunsch style)
// Aligns user lyric words to Whisper words to inherit their timestamps.
// ---------------------------------------------------------------------------

function dpAlign(userWords, whisperWords) {
  const n = userWords.length;
  const m = whisperWords.length;
  const MATCH = 2, MISMATCH = -1, GAP = -1;

  // Build DP table
  const dp = Array.from({ length: n + 1 }, () => new Float32Array(m + 1));
  const trace = Array.from({ length: n + 1 }, () => new Uint8Array(m + 1)); // 0=none,1=diag,2=up,3=left

  for (let i = 1; i <= n; i++) { dp[i][0] = i * GAP; trace[i][0] = 2; }
  for (let j = 1; j <= m; j++) { dp[0][j] = j * GAP; trace[0][j] = 3; }

  const uNorm = userWords.map(w => normalize(w.word));
  const wNorm = whisperWords.map(w => normalize(w.word));

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const matchScore = uNorm[i - 1] === wNorm[j - 1] ? MATCH : MISMATCH;
      const diag = dp[i - 1][j - 1] + matchScore;
      const up   = dp[i - 1][j] + GAP;
      const left = dp[i][j - 1] + GAP;
      if (diag >= up && diag >= left) { dp[i][j] = diag; trace[i][j] = 1; }
      else if (up >= left)            { dp[i][j] = up;   trace[i][j] = 2; }
      else                            { dp[i][j] = left; trace[i][j] = 3; }
    }
  }

  // Traceback → alignment pairs
  const pairs = []; // { userIdx, whisperIdx | null }
  let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && trace[i][j] === 1) {
      pairs.unshift({ userIdx: i - 1, whisperIdx: j - 1 });
      i--; j--;
    } else if (i > 0 && trace[i][j] === 2) {
      pairs.unshift({ userIdx: i - 1, whisperIdx: null }); // user word not in whisper
      i--;
    } else {
      j--; // whisper word not in user lyrics — skip
    }
  }
  return pairs;
}

/**
 * Assign timestamps to each user lyric word via DP alignment to Whisper words.
 * Gaps (unmatched user words) are filled by interpolation between neighbours.
 *
 * @returns {Array<{start: number, end: number}>} one entry per user word
 */
function assignTimestamps(userWords, whisperWords) {
  const n = userWords.length;
  const pairs = dpAlign(userWords, whisperWords);

  const timestamps = new Array(n).fill(null);

  for (const { userIdx, whisperIdx } of pairs) {
    if (whisperIdx !== null) {
      const w = whisperWords[whisperIdx];
      timestamps[userIdx] = { start: w.start, end: w.end };
    }
  }

  // Interpolate / extrapolate gaps
  for (let k = 0; k < n; k++) {
    if (timestamps[k]) continue;

    let prevK = -1, nextK = n;
    for (let p = k - 1; p >= 0; p--) { if (timestamps[p]) { prevK = p; break; } }
    for (let p = k + 1; p < n;  p++) { if (timestamps[p]) { nextK = p; break; } }

    const AVG_WORD_SEC = 0.35;
    if (prevK >= 0 && nextK < n) {
      const t0 = timestamps[prevK].end;
      const t1 = timestamps[nextK].start;
      const frac = (k - prevK) / (nextK - prevK);
      const t = t0 + frac * (t1 - t0);
      timestamps[k] = { start: t, end: t };
    } else if (prevK >= 0) {
      const t = timestamps[prevK].end + (k - prevK) * AVG_WORD_SEC;
      timestamps[k] = { start: t, end: t };
    } else if (nextK < n) {
      const t = Math.max(0, timestamps[nextK].start - (nextK - k) * AVG_WORD_SEC);
      timestamps[k] = { start: t, end: t };
    } else {
      timestamps[k] = { start: 0, end: 0 };
    }
  }

  return timestamps;
}

// ---------------------------------------------------------------------------
// Primary export
// ---------------------------------------------------------------------------

/**
 * Align madmom chords (second-based timestamps) to user lyric words.
 *
 * If whisperWords are provided, the user's lyrics are DP-aligned to the Whisper
 * word list so each lyric word inherits a timestamp, then each chord is placed
 * on the nearest lyric word by time. This handles Whisper mis-hearings, repeated
 * lines, and partial transcriptions gracefully.
 *
 * Chords that occur before the first Whisper word are placed at lineIndex = -1
 * (a virtual Intro section rendered by ChordSheetDisplay).
 *
 * If no whisperWords, chords are distributed proportionally.
 *
 * @param {Array<{time: number, chord: string}>} chords   madmom output (seconds)
 * @param {string}  userLyrics  raw lyrics text (may include [Section] headers)
 * @param {Array<{word: string, start: number, end: number}>} [whisperWords]
 * @returns {Array<{chord: string, lineIndex: number, wordIndex: number}>}
 */
export function alignChordsToLyrics(chords, userLyrics, whisperWords, opts = {}) {
  if (!chords || chords.length === 0) return { alignment: [], orphans: [] };

  const userWordList = parseLyricsWords(userLyrics);
  if (userWordList.length === 0) return { alignment: [], orphans: [] };

  let timestamps;
  let wordTimingRepairs = [];
  let firstWordTime = 0;
  let lastWordTime = null;

  if (whisperWords && whisperWords.length > 0) {
    const validWhisper = whisperWords.filter(w => w.start != null && w.end != null);
    const pairs = dpAlign(userWordList, validWhisper);
    timestamps = assignTimestamps(userWordList, validWhisper);
    const timingRepair = repairPostInstrumentalWordTimings(
      userWordList,
      timestamps,
      userLyrics,
      chords,
      opts,
    );
    timestamps = timingRepair.timestamps;
    wordTimingRepairs = timingRepair.repairs;

    // Use the FIRST aligned user↔whisper match as intro boundary —
    // not raw validWhisper[0] which may be a false detection before the song starts.
    for (const { whisperIdx } of pairs) {
      if (whisperIdx !== null && validWhisper[whisperIdx]) {
        firstWordTime = validWhisper[whisperIdx].start;
        break;
      }
    }

    // Find LAST aligned match for outro boundary
    for (let k = pairs.length - 1; k >= 0; k--) {
      const { whisperIdx } = pairs[k];
      if (whisperIdx !== null && validWhisper[whisperIdx]) {
        lastWordTime = validWhisper[whisperIdx].end;
        break;
      }
    }
    // Boundary should be first timed lyric word, regardless of DP start drift.
    const firstTimed = (timestamps || []).find((ts) => ts && ts.start != null);
    if (firstTimed && Number.isFinite(Number(firstTimed.start))) {
      firstWordTime = Number(firstTimed.start);
    }
  } else {
    const totalDuration = chords[chords.length - 1]?.time || 1;
    const step = totalDuration / userWordList.length;
    timestamps = userWordList.map((_, idx) => ({
      start: idx * step,
      end: (idx + 1) * step,
    }));
    firstWordTime = 0;
  }

  const result = [];
  const orphans = [];
  const introAssignmentDiagnostics = {
    introBoundaryTime: firstWordTime ?? null,
    introAssignedCount: 0,
  };

  // Detect [Instrumental] sections and their time ranges using the DP-aligned timestamps.
  // Chords whose time falls inside these ranges are routed to orphans so that
  // injectSpacers can place them under the [Instrumental] header line instead of
  // pulling them to the nearest bridge/verse word.
  //
  // Boundaries: t_start = last-lyric-word-end + 1/16th note
  //             t_end   = first-lyric-word-start - 1/16th note
  // The 1/16th offset keeps chords that are on the exact word-boundary beat
  // attached to their lyric word rather than misrouted to instrumental.
  const sixteenthSec = opts?.sixteenthSec ?? 0.125;
  const instRanges = [];
  if (whisperWords && whisperWords.length > 0 && timestamps) {
    const lyricLinesArr = userLyrics ? userLyrics.split('\n') : [];
    lyricLinesArr.forEach((line, li) => {
      if (!/^\[(instrumental|solo|guitar\s+solo)\b/i.test(line.trim())) return;
      let tStart = -Infinity;
      let tEnd = Infinity;
      for (let k = 0; k < userWordList.length; k++) {
        const ts = timestamps[k];
        if (!ts) continue;
        if (userWordList[k].lineIndex < li && ts.end != null) {
          const e = Number(ts.end);
          if (e > tStart) tStart = e;
        }
        if (userWordList[k].lineIndex > li && ts.start != null) {
          const s = Number(ts.start);
          if (s < tEnd) tEnd = s;
        }
      }
      if (tStart > -Infinity && tEnd < Infinity && tStart < tEnd) {
        instRanges.push({ li, t_start: tStart + sixteenthSec, t_end: tEnd - sixteenthSec });
      }
    });
  }

  const placed = new Set();
  const wordAnchorCounts = new Map();
  let introWordIdx = 0;
  let outroWordIdx = 0;
  let lastPlacedChord = null; // track last chord placed to suppress same-chord continuations

  for (const { time, chord } of chords) {
    if (!chord) continue;

    const hasWhisper = whisperWords && whisperWords.length > 0;

    // Chords before the first timed lyric word → Intro (lineIndex = -1).
    // Section-aware boundary avoids stem-mode timing drift from hard-coded -1s gates.
    if (hasWhisper && firstWordTime != null && time < firstWordTime) {
      // Skip if same chord as previous (continuation, not a new beat)
      if (chord === lastPlacedChord) continue;
      const key = `intro:${introWordIdx}`;
      if (!placed.has(key)) {
        placed.add(key);
        result.push({ chord, lineIndex: -1, wordIndex: introWordIdx, time });
        introWordIdx++;
        lastPlacedChord = chord;
        introAssignmentDiagnostics.introAssignedCount += 1;
      }
      continue;
    }

    // Chords after last aligned lyric → Outro (lineIndex = -2)
    if (hasWhisper && lastWordTime !== null && time > lastWordTime + 1.0) {
      // Skip if same chord as what was last playing (no new beat)
      if (chord === lastPlacedChord) continue;
      const key = `outro:${outroWordIdx}`;
      if (!placed.has(key)) {
        placed.add(key);
        result.push({ chord, lineIndex: -2, wordIndex: outroWordIdx, time });
        outroWordIdx++;
        lastPlacedChord = chord;
      }
      continue;
    }

    // Chords falling in an [Instrumental] time range → orphan so injectSpacers
    // places them under the section header rather than pulling them to the
    // nearest bridge or verse word.
    if (hasWhisper && instRanges.length > 0) {
      const ir = instRanges.find(r => time >= r.t_start && time <= r.t_end);
      if (ir) {
        orphans.push({ chord, time });
        continue;
      }
    }

    // Normal placement: closest user word by timestamp
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let k = 0; k < userWordList.length; k++) {
      const ts = timestamps[k];
      if (!ts) continue;
      let dist;
      if (time >= ts.start && time <= ts.end) {
        dist = 0;
      } else {
        dist = Math.min(Math.abs(time - ts.start), Math.abs(time - ts.end));
      }
      if (dist < bestDist) { bestDist = dist; bestIdx = k; }
      if (dist === 0) break;
    }

    const { lineIndex, wordIndex } = userWordList[bestIdx];
    const key = `${lineIndex}:${wordIndex}`;
    const count = Number(wordAnchorCounts.get(key) || 0);
    wordAnchorCounts.set(key, count + 1);
    placed.add(key);
    const entry = { chord, lineIndex, wordIndex, time };
    if (count > 0) entry.syllableOffset = count;
    result.push(entry);
    lastPlacedChord = chord;
  }

  return { alignment: result, orphans, introAssignmentDiagnostics, wordTimingRepairs };
}

// ---------------------------------------------------------------------------
// Section-start chord insertion
// ---------------------------------------------------------------------------

/**
 * After initial chord alignment, ensure every section start has an explicit
 * chord entry.  When the same chord carries forward across a section boundary
 * it can be easy to miss in the score.  This function adds an explicit entry
 * at the first word of each section if one is not already present, using the
 * word's Whisper start time so playback highlighting works correctly.
 *
 * @param {Array}  alignment    Result of alignChordsToLyrics
 * @param {string} lyrics       Raw lyrics text (with [Section] headers)
 * @param {Array}  timedLyrics  Result of computeTimedLyrics (word times)
 * @returns {Array}             Updated alignment (may have additional entries)
 */
export function addSectionStartChords(alignment, lyrics, timedLyrics, bpm = null, beatsPerMeasure = null, beats = []) {
  if (!alignment || alignment.length === 0 || !timedLyrics || timedLyrics.length === 0) {
    return alignment;
  }

  // Build word-time lookup
  const wordTimeMap = {};
  for (const w of timedLyrics) {
    if (w.start != null) wordTimeMap[`${w.lineIndex}:${w.wordIndex}`] = w.start;
  }

  // Existing alignment keyed by "lineIndex:wordIndex"
  const alignedKeys = new Set(alignment.map(e => `${e.lineIndex}:${e.wordIndex}`));

  // Sort by time for binary-search-style lookup
  const timedAligned = alignment.filter(e => e.time != null && e.lineIndex >= 0)
    .sort((a, b) => a.time - b.time);

  // Find the chord playing at a given time (last chord entry with time <= t)
  const chordAtTime = (t) => {
    let last = null;
    for (const e of timedAligned) {
      if (e.time <= t) last = e;
      else break;
    }
    return last;
  };

  // Downbeats (beat 1 of each measure) sorted by time
  const downbeats = beats.filter(b => b.beat === 1).sort((a, b) => a.time - b.time);
  const measureDur = (bpm && beatsPerMeasure) ? (60 / bpm) * beatsPerMeasure : null;

  // All beats sorted — used to find the last beat at or before a word start
  const allBeatsSorted = [...beats].sort((a, b) => a.time - b.time);
  const beatBefore = (t) => {
    let last = null;
    for (const b of allBeatsSorted) {
      if (b.time <= t) last = b;
      else break;
    }
    return last;
  };

  // Find the nearest downbeat within ±1 measure of time t.
  // Prefers the downbeat at-or-before t (the measure the section starts on).
  const nearestDownbeat = (t) => {
    if (!downbeats.length) return null;
    const window = measureDur || 2.0;
    let best = null, bestDiff = Infinity;
    for (const db of downbeats) {
      const diff = Math.abs(db.time - t);
      if (diff < bestDiff && diff <= window) { bestDiff = diff; best = db; }
    }
    return best;
  };

  // Parse section boundaries: find first lyric-line lineIndex after each header
  const lines = lyrics.split('\n');
  const additions = [];

  let prevLineWasHeader = false;
  for (let li = 0; li < lines.length; li++) {
    const trimmed = lines[li].trim();
    if (!trimmed) { prevLineWasHeader = false; continue; }
    if (/^\[.+\]/.test(trimmed)) { prevLineWasHeader = true; continue; }

    if (prevLineWasHeader) {
      prevLineWasHeader = false;
      // li is the first lyric line of a new section
      const wordTime = wordTimeMap[`${li}:0`];
      if (wordTime == null) continue; // no timing for this line

      // --- Beat-aware placement ---
      // Find the downbeat of the measure the section starts in (the "entry downbeat").
      // Using the entry downbeat as the chord-lookup anchor correctly handles pickup
      // chords (chords that start on beat 4 of the preceding measure) — they are
      // already the chord "at beat 1 of the new section."
      const db = nearestDownbeat(wordTime);
      const targetTime = db ? db.time : wordTime;

      // Find what chord is playing AT the entry downbeat (not just before word 0).
      const source = chordAtTime(targetTime);
      if (!source) continue;

      // Look at the first detected chord in the new section
      const firstOnLine = alignment
        .filter(e => e.lineIndex === li && e.time != null && e.chord)
        .sort((a, b) => a.wordIndex - b.wordIndex)[0];

      // Skip if there's already a detected chord at word 0 or 1
      if (firstOnLine && firstOnLine.wordIndex <= 1) continue;

      // Skip if same chord (would be redundant)
      if (firstOnLine && firstOnLine.chord === source.chord) continue;

      // Among timed words on this line, find the word closest to the downbeat
      const lineWords = timedLyrics
        .filter(w => w.lineIndex === li && w.start != null)
        .sort((a, b) => a.wordIndex - b.wordIndex);

      let bestWord = null, bestDiff = Infinity;
      for (const w of lineWords) {
        const diff = Math.abs(w.start - targetTime);
        if (diff < bestDiff) { bestDiff = diff; bestWord = w; }
      }

      if (!bestWord) continue;

      // Don't place if that word slot already has a chord
      if (alignedKeys.has(`${bestWord.lineIndex}:${bestWord.wordIndex}`)) continue;

      // Anchor carry-forward time to the last beat at or before the target word,
      // so highlighting fires at the beat just before the new section starts.
      const anchorBeat = beatBefore(bestWord.start);
      const carryTime = anchorBeat ? anchorBeat.time : bestWord.start;

      additions.push({
        chord: source.chord,
        lineIndex: bestWord.lineIndex,
        wordIndex: bestWord.wordIndex,
        time: carryTime,
        sectionCarryForward: true,
      });
    } else {
      prevLineWasHeader = false;
    }
  }

  if (additions.length === 0) return alignment;
  return [...alignment, ...additions];
}

// ---------------------------------------------------------------------------
// Fallback: even distribution (used for MIDI and no-lyrics audio)
// ---------------------------------------------------------------------------

/**
 * Returns per-word timing data for debug/diagnostic purposes.
 * Aligns user lyric words to Whisper words and returns each word with
 * its assigned timestamp so you can see how the fuzzy matching performed.
 *
 * @param {string} userLyrics
 * @param {Array<{word, start, end}>} whisperWords
 * @returns {Array<{lineIndex, wordIndex, word, start, end, matched}>}
 */
export function computeTimedLyrics(userLyrics, whisperWords, opts = {}) {
  if (!whisperWords || whisperWords.length === 0) return [];
  const userWordList = parseLyricsWords(userLyrics);
  if (userWordList.length === 0) return [];

  const validWhisper = whisperWords.filter(w => w.start != null && w.end != null);
  const pairs = dpAlign(userWordList, validWhisper);
  const initialTimestamps = assignTimestamps(userWordList, validWhisper);
  const timingRepair = repairPostInstrumentalWordTimings(
    userWordList,
    initialTimestamps,
    userLyrics,
    opts.chords || [],
    opts,
  );
  const timestamps = timingRepair.timestamps;

  const timedLyrics = userWordList.map((uw, idx) => {
    const ts = timestamps[idx] || { start: null, end: null };
    const pair = pairs.find(p => p.userIdx === idx);
    const matched = pair && pair.whisperIdx !== null
      ? validWhisper[pair.whisperIdx].word
      : null;
    return {
      lineIndex: uw.lineIndex,
      wordIndex: uw.wordIndex,
      word: uw.word,
      start: ts.start,
      end: ts.end,
      matchedWhisperWord: matched,
    };
  });
  timedLyrics.wordTimingRepairs = timingRepair.repairs;
  return timedLyrics;
}

/**
 * Naive even distribution: spread chords proportionally across lyric lines and
 * distribute multiple chords within a line across different word positions.
 *
 * @param {string[]} chords
 * @param {string} lyrics
 * @returns {Array<{chord: string, lineIndex: number, wordIndex: number}>}
 */
export function evenDistribute(chords, lyrics) {
  const lines = lyrics.split('\n');
  const lyricLines = [];
  lines.forEach((line, li) => {
    const isSectionHeader = /^\[.+\]/.test(line.trim());
    const isBlank = line.trim() === '';
    if (!isSectionHeader && !isBlank) {
      const words = line.trim().split(/\s+/).filter(Boolean);
      lyricLines.push({ lineIndex: li, line, wordCount: words.length });
    }
  });

  if (lyricLines.length === 0 || chords.length === 0) return { alignment: [], orphans: [] };

  const slots = [];
  lyricLines.forEach(({ lineIndex, wordCount }) => {
    for (let w = 0; w < wordCount; w++) {
      slots.push({ lineIndex, wordIndex: w });
    }
  });

  const result = [];
  const placed = new Set();
  chords.forEach((chord, ci) => {
    const slotIdx = Math.min(
      Math.floor((ci / chords.length) * slots.length),
      slots.length - 1
    );
    const { lineIndex, wordIndex } = slots[slotIdx];
    const key = `${lineIndex}:${wordIndex}`;
    if (!placed.has(key)) {
      placed.add(key);
      result.push({ chord, lineIndex, wordIndex });
    }
  });

  return { alignment: result, orphans: [] };
}

// ---------------------------------------------------------------------------
// Spacer injection — creates synthetic chord slots for orphaned chords
// ---------------------------------------------------------------------------

/**
 * For each orphaned chord (one that alignChordsToLyrics could not place),
 * find the correct line and fractional wordIndex using timedLyrics, then
 * inject a spacer entry into the alignment.
 *
 * Spacer entries: { chord, lineIndex, wordIndex: float, time, isSpacer: true }
 *   wordIndex < 0        → before word 0
 *   N < wordIndex < N+1  → between word N and N+1
 *   wordIndex > lastWI   → after last word
 *
 * Only runs when timedLyrics is non-empty (requires ctc-forced-aligner or Whisper).
 *
 * @param {Array<{chord:string, time:number}>} orphans
 * @param {Array<object>} alignment  existing alignment entries
 * @param {Array<{lineIndex:number, wordIndex:number, start:number, end:number}>} timedLyrics
 * @param {string} userLyrics  raw lyrics text (for section-header detection)
 * @returns {Array<object>}  alignment merged with spacer entries
 */
export function injectSpacers(orphans, alignment, timedLyrics, userLyrics) {
  if (!orphans || orphans.length === 0) return alignment;
  if (!timedLyrics || timedLyrics.length === 0) return alignment;

  const lyricLines = userLyrics ? userLyrics.split('\n') : [];

  // Pre-compute section header line indices so we never put a spacer there
  const headerLines = new Set();
  lyricLines.forEach((line, li) => {
    if (/^\[.+\]/.test(line.trim())) headerLines.add(li);
  });

  // Detect [Instrumental] headers and their time ranges
  // Range: from end of last word before the header to start of first word after it
  const instrumentalRanges = []; // [{li, t_start, t_end}]
  lyricLines.forEach((line, li) => {
    if (!/^\[(instrumental|solo|guitar\s+solo)\b/i.test(line.trim())) return;
    // Find last word before this header
    let t_start = -Infinity;
    for (const w of timedLyrics) {
      if (w.lineIndex < li && w.start != null) {
        const wEnd = w.end ?? w.start;
        if (wEnd > t_start) t_start = wEnd;
      }
    }
    // Find first word after this header
    let t_end = Infinity;
    for (const w of timedLyrics) {
      if (w.lineIndex > li && w.start != null) {
        if (w.start < t_end) t_end = w.start;
      }
    }
    instrumentalRanges.push({ li, t_start, t_end });
  });

  // Build a map: lineIndex → sorted array of {wordIndex, start, end}
  const lineWordMap = {};
  for (const w of timedLyrics) {
    if (w.start == null) continue;
    if (!lineWordMap[w.lineIndex]) lineWordMap[w.lineIndex] = [];
    lineWordMap[w.lineIndex].push({ wordIndex: w.wordIndex, start: w.start, end: w.end ?? w.start });
  }
  for (const li of Object.keys(lineWordMap)) {
    lineWordMap[li].sort((a, b) => a.wordIndex - b.wordIndex);
  }

  // Build set of existing real-chord keys so we don't duplicate
  const realKeys = new Set(
    alignment.filter(e => !e.isSpacer).map(e => `${e.lineIndex}:${e.wordIndex}`)
  );
  // Track spacer positions already used (to avoid stacking two spacers at same float)
  const spacerPositions = new Set(
    alignment.filter(e => e.isSpacer).map(e => `${e.lineIndex}:${e.wordIndex}`)
  );

  // Sorted line index list for fallback (nearest line search)
  const sortedLineIndices = Object.keys(lineWordMap).map(Number).sort((a, b) => a - b);

  const newSpacers = [];

  // Track how many instrumental chords have been assigned per section header line
  const instrumentalCount = {};

  for (const { chord, time } of orphans) {
    // Check if this orphan falls in an instrumental section time range
    const instRange = instrumentalRanges.find(r => time >= r.t_start && time <= r.t_end);
    if (instRange) {
      const n = instrumentalCount[instRange.li] ?? 0;
      instrumentalCount[instRange.li] = n + 1;
      newSpacers.push({ chord, lineIndex: instRange.li, wordIndex: n, time, isInstrumental: true });
      continue;
    }

    // 1. Find the best line: line whose words are closest in time to this chord
    let bestLine = null;
    let bestLineDist = Infinity;
    for (const li of sortedLineIndices) {
      if (headerLines.has(li)) continue;
      const words = lineWordMap[li];
      for (const w of words) {
        const d = Math.min(Math.abs(time - w.start), Math.abs(time - (w.end ?? w.start)));
        if (d < bestLineDist) { bestLineDist = d; bestLine = li; }
      }
    }
    if (bestLine === null) continue;

    const words = lineWordMap[bestLine];

    // 2. Compute fractional wordIndex
    let wi;
    if (time < words[0].start) {
      // Before first word on this line
      wi = words[0].wordIndex - 0.5;
    } else if (time > (words[words.length - 1].end ?? words[words.length - 1].start)) {
      // After last word
      wi = words[words.length - 1].wordIndex + 0.5;
    } else {
      // Between words: find first word that starts after the chord time
      const nextIdx = words.findIndex(w => w.start > time);
      if (nextIdx <= 0) {
        wi = words[words.length - 1].wordIndex + 0.5;
      } else {
        const before = words[nextIdx - 1].wordIndex;
        const after = words[nextIdx].wordIndex;
        wi = (before + after) / 2;
      }
    }

    // 3. Avoid collisions with existing real chords and other spacers
    const realKey = `${bestLine}:${Math.round(wi)}`; // nearest integer
    if (realKeys.has(realKey)) {
      // Shift away from the real word
      wi = wi - 0.15;
    }
    // Avoid stacking spacers at the same position (nudge by small delta)
    let attempt = wi;
    while (spacerPositions.has(`${bestLine}:${attempt}`)) {
      attempt = Math.round((attempt - 0.15) * 1000) / 1000;
    }
    wi = attempt;

    spacerPositions.add(`${bestLine}:${wi}`);
    newSpacers.push({ chord, lineIndex: bestLine, wordIndex: wi, time, isSpacer: true });
  }

  return [...alignment, ...newSpacers];
}
